import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as RevenueIcon,
  TrendingUp as GrowthIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { adminDashboardApi } from '../services/api';
import { AdminDashboardStats } from '../types';
import Logo from '../../components/Logo';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend, subtitle }) => (
  <Card sx={{ 
    borderRadius: 3, 
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': { transform: 'translateY(-5px)' }
  }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color, fontSize: 40 }}>
          {icon}
        </Box>
      </Box>
      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={trend.isPositive ? <TrendingUpIcon /> : <TrendingUpIcon sx={{ transform: 'rotate(180deg)' }} />}
            label={`${trend.isPositive ? '+' : ''}${trend.value}%`}
            color={trend.isPositive ? 'success' : 'error'}
            size="small"
          />
          <Typography variant="body2" color="textSecondary">
            vs last month
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userGrowthData, setUserGrowthData] = useState<any>({ labels: [], datasets: [] });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const statsResponse = await adminDashboardApi.getStats();
        if (statsResponse.status === 'success' && statsResponse.data) {
          setStats(statsResponse.data);
        } else {
          setError(statsResponse.message || 'Failed to fetch dashboard stats');
        }

        // Mock data for charts since API methods don't exist yet
        setUserGrowthData({
          labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
          datasets: [
            {
              label: 'New Users',
              data: [12, 19, 15, 25, 22, 30],
              borderColor: '#42a5f5',
              backgroundColor: 'rgba(66, 165, 245, 0.2)',
              fill: true,
              tension: 0.4,
            },
          ],
        });

        // Mock recent orders data
        setRecentOrders([
          { id: 1, order_number: 'ORD-001', customer_name: 'John Doe', total_amount: '1500', status: 'completed' },
          { id: 2, order_number: 'ORD-002', customer_name: 'Jane Smith', total_amount: '2300', status: 'pending' },
          { id: 3, order_number: 'ORD-003', customer_name: 'Bob Johnson', total_amount: '1800', status: 'delivered' },
        ]);
      } catch (err: any) {
        setError(err.response?.data?.message || 'An unexpected error occurred');
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => window.location.reload()} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
        gap: 3, 
        mb: 4 
      }}>
        <StatCard
          title="Total Users"
          value={stats?.total_users || 0}
          icon={<PeopleIcon />}
          color="#1976d2"
          trend={stats?.user_growth ? {
            value: stats.user_growth.growth_percentage,
            isPositive: stats.user_growth.growth_percentage >= 0
          } : undefined}
          subtitle={`${stats?.active_users || 0} active`}
        />
        <StatCard
          title="Total Orders"
          value={stats?.total_orders || 0}
          icon={<OrdersIcon />}
          color="#2e7d32"
          trend={stats?.order_analytics ? {
            value: stats.order_analytics.growth_percentage,
            isPositive: stats.order_analytics.growth_percentage >= 0
          } : undefined}
          subtitle={`${stats?.completed_orders || 0} completed`}
        />
        <StatCard
          title="Monthly Revenue"
          value={`₹${stats?.monthly_revenue || '0'}`}
          icon={<RevenueIcon />}
          color="#ed6c02"
          subtitle={`Total: ₹${stats?.total_revenue || '0'}`}
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.subscription_analytics?.active_subscriptions || 0}
          icon={<SecurityIcon />}
          color="#9c27b0"
          subtitle={`${stats?.subscription_analytics?.renewal_rate || 0}% renewal rate`}
        />
      </Box>

      {/* Charts Section */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
        gap: 3, 
        mb: 4 
      }}>
        {/* User Growth Chart */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Growth (Last 30 Days)
            </Typography>
            <Box sx={{ height: 300 }}>
              <Line
                data={userGrowthData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' },
                    title: { display: false },
                  },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } },
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Subscription Status */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Orders
            </Typography>
            <List>
              {recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <ListItem>
                      <ListItemIcon>
                        <OrdersIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Order #${order.order_number}`}
                        secondary={`Customer: ${order.customer_name} | Total: ₹${order.total_amount} | Status: ${order.status}`}
                      />
                      <Chip
                        label={order.status}
                        color={
                          order.status === 'completed' || order.status === 'delivered'
                            ? 'success'
                            : order.status === 'pending'
                            ? 'warning'
                            : 'info'
                        }
                        size="small"
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                  No recent orders.
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>
      </Box>


      {/* Quick Actions */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
        gap: 3, 
        mt: 2 
      }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PeopleIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Manage Users" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <OrdersIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="View Orders" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="System Settings" />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">CPU Usage</Typography>
                <Typography variant="body2" color="success.main">45%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={45} color="success" />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Memory Usage</Typography>
                <Typography variant="body2" color="warning.main">75%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={75} color="warning" />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminDashboard;